/**
 * Schedule Detail Form Content — mirrors POLineDetailTabContent design
 * so the Schedule detail modal matches the PO Line detail modal layout.
 *
 * Two-column grid:
 *   - Left card: labelled read-only inputs (supplier, sched line #, qty, dates,
 *     part info, prices, status chips, on-hold banner, proposed-change banner)
 *   - Right card: Audit History changelog
 */
import { Calendar } from 'lucide-react';
import type { POLineSchedule } from '../../lib/api/types/purchasing';
import Changelog, { ChangelogEntry } from '../common/Changelog';
import { useFormatters } from '../../contexts/PreferencesContext';
import { ORIGINAL_REQUIRED_DATE, SUPPLIER_PROMISE_DATE } from '../../config/dateLabels';

interface ScheduleDetailFormContentProps {
  schedule: POLineSchedule;
  changelogs: ChangelogEntry[];
  formatDate: (d: string | undefined) => string;
  statusBadge: { label: string; cls: string };
  ackBadge: { label: string; cls: string };
}

/**
 * Who raised the quantity sitting in the staging slot.
 *
 * `supplier_proposed_qty` is the column name but NOT the meaning: the schedule
 * grain lets EITHER side propose a quantity and both write that one field
 * (ScheduleWorkflowService.propose_quantity_change). Labelling it "Supplier
 * Proposed Qty" therefore attributed the customer's own request to the
 * supplier — and did it beside an audit row that read "Customer proposed
 * quantity change", so the modal contradicted itself. The accept dialog has
 * always named the raiser correctly ("PROPOSED CHANGE (CUSTOMER)"); this makes
 * the field agree with it.
 */
function proposedQtyLabel(s: POLineSchedule): string {
  if (!s.supplier_proposed_qty) return 'Proposed Qty';
  if (s.acknowledge_status === 'change_proposed_customer') return 'Customer Proposed Qty';
  if (s.acknowledge_status === 'change_proposed_supplier') return 'Supplier Proposed Qty';
  return 'Proposed Qty';
}

const READONLY_INPUT_CLS =
  'w-full px-3 py-2 border border-border rounded-lg bg-muted/30 font-medium';
const DATE_FIELD_WRAP_CLS =
  'flex items-center gap-2 border border-border rounded-lg bg-muted/30 px-3 py-2';
const DATE_INPUT_CLS =
  'flex-1 bg-transparent border-none outline-none font-medium';

export function ScheduleDetailFormContent({
  schedule,
  changelogs,
  formatDate,
  statusBadge,
  ackBadge,
}: ScheduleDetailFormContentProps) {
  const { formatQuantity } = useFormatters();
  const s = schedule;
  // CR-7262: shared formatter; its own dash fallback matches what this had.
  const fmtQty = (q: string | undefined) => formatQuantity(q);

  return (
    <div className="grid grid-cols-2 gap-4">
      {/* LEFT — Schedule fields card */}
      <div className="card p-4 border rounded-lg">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">Supplier</label>
            <input type="text" value={s.supplier_name || ''} readOnly className={READONLY_INPUT_CLS} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-1">Schedule Line #</label>
              <input type="text" value={s.schedule_line_number || '-'} readOnly className={READONLY_INPUT_CLS} />
            </div>
            <div>
              <label className="block text-sm font-medium text-primary mb-1">Part Number</label>
              <input type="text" value={s.part_number || ''} readOnly className={`${READONLY_INPUT_CLS} font-mono`} />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">Part Description</label>
            {/* CR-7308 rev 2: the master, not the line's order text. */}
            <input type="text" value={s.part_description || ''} readOnly className={READONLY_INPUT_CLS} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-1">{ORIGINAL_REQUIRED_DATE}</label>
              <div className={DATE_FIELD_WRAP_CLS}>
                <Calendar className="h-4 w-4 text-muted-foreground" />
                {/* Mirror Schedule Date until the supplier acknowledges
                    (i.e. the "original promise" is the customer's
                    requested date at creation time). After first
                    acknowledgement the FK gets populated and we display
                    the actual locked value. */}
                {/* R-378, remaining half: this re-derives the ODD from the
                    schedule's own columns. The line payload carries a
                    grain-aware `odd_date` from the server; the SCHEDULE
                    payload does not, so there is nothing correct to read
                    yet. Left as-is deliberately rather than guessed —
                    adding odd_date to the schedule serializer is its own
                    change with its own evidence. */}
                <input type="text" value={formatDate(s.original_required_date || s.schedule_date)} readOnly className={DATE_INPUT_CLS} />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-primary mb-1">Schedule Date</label>
              <div className={DATE_FIELD_WRAP_CLS}>
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <input type="text" value={formatDate(s.schedule_date)} readOnly className={DATE_INPUT_CLS} />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">{SUPPLIER_PROMISE_DATE}</label>
            <div className={DATE_FIELD_WRAP_CLS}>
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <input type="text" value={formatDate(s.supplier_promise_date)} readOnly className={DATE_INPUT_CLS} />
            </div>
          </div>

          {/* Qty fields grouped together: Schedule Qty (required) → Received
              Qty (already delivered across all GRNs) → Balance Qty (still due).
              Schedule Qty = Received Qty + Balance Qty. */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium text-primary mb-1">Schedule Qty</label>
              <input type="text" value={fmtQty(s.schedule_qty)} readOnly className={READONLY_INPUT_CLS} />
            </div>
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-1">Received Qty</label>
              <input type="text" value={fmtQty(s.shipped_qty)} readOnly className={READONLY_INPUT_CLS} />
            </div>
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-1">Balance Qty</label>
              <input
                type="text"
                value={fmtQty(s.balance_qty)}
                readOnly
                className={`${READONLY_INPUT_CLS} ${parseFloat(s.balance_qty || '0') > 0 ? 'text-orange-600' : ''}`}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-1">Unit Price</label>
              <input
                type="text"
                value={s.unit_price ? parseFloat(s.unit_price).toLocaleString(undefined, { minimumFractionDigits: 3 }) : '-'}
                readOnly
                className={READONLY_INPUT_CLS}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-1">{proposedQtyLabel(s)}</label>
              <input type="text" value={fmtQty(s.supplier_proposed_qty)} readOnly className={READONLY_INPUT_CLS} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-1">Status</label>
              <div className="px-3 py-2 border border-border rounded-lg bg-muted/30">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusBadge.cls}`}>{statusBadge.label}</span>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-1">Acknowledge Status</label>
              <div className="px-3 py-2 border border-border rounded-lg bg-muted/30">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${ackBadge.cls}`}>{ackBadge.label}</span>
              </div>
            </div>
          </div>

          {s.on_hold && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm font-semibold text-red-800 mb-1">On Hold</p>
              <p className="text-sm text-red-700">{s.on_hold_reason_text || 'No reason provided'}</p>
              {s.on_hold_date && (
                <p className="text-xs text-red-600 mt-1">Since {formatDate(s.on_hold_date)}</p>
              )}
            </div>
          )}

          {s.supplier_proposed_date && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm font-semibold text-blue-800 mb-1">Pending Proposal</p>
              <p className="text-sm text-blue-700">Proposed Date: {formatDate(s.supplier_proposed_date)}</p>
            </div>
          )}
        </div>
      </div>

      {/* RIGHT — Audit History card */}
      <div>
        <Changelog grain="schedule" entries={changelogs} emptyMessage="No audit history available" />
      </div>
    </div>
  );
}
